import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";

import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from "@/components/ui/resizable";
import type { GroupImperativeHandle, PanelImperativeHandle } from "react-resizable-panels";
import { InspectorPane } from "@/components/workspace/InspectorPane";
import { useInspector } from "@/contexts/InspectorContext";
import { useMediaQuery } from "@/hooks/useMediaQuery";

interface WorkspaceShellContextValue {
  inspectorEnabled: boolean;
  inspectorOpen: boolean;
  toggleInspector: () => void;
}

const WorkspaceShellContext = createContext<WorkspaceShellContextValue | null>(null);

export function useWorkspaceShell(): WorkspaceShellContextValue {
  const context = useContext(WorkspaceShellContext);
  if (!context) {
    throw new Error("useWorkspaceShell must be used within WorkspaceShell");
  }
  return context;
}

const DESKTOP_QUERY = "(min-width: 768px)";

interface WorkspaceShellProps {
  /** Thin conversation top bar; rendered above the panel group. */
  topBar?: ReactNode;
  /** Enables the right-hand inspector pane for this surface. */
  inspector?: boolean;
  /** Initial open state for inspector-enabled surfaces. */
  defaultInspectorOpen?: boolean;
  /** localStorage key for persisting panel sizes (namespaced, e.g. "vadium.panelSize.assistant"). */
  autoSaveId: string;
  /** The conversation / center content. */
  children: ReactNode;
}

/**
 * The three-pane workspace shell. The left nav rail (AppSidebar) lives in the
 * authenticated layout and is NOT part of this panel group. Here we lay out the
 * center conversation and the right-hand inspector as sibling resizable panels
 * on desktop; on mobile (`< md`) the inspector renders as a full-screen overlay
 * and the center content fills the width.
 *
 * `children` is rendered exactly once (always as the center panel) so the
 * streaming conversation subtree is never remounted when crossing the
 * desktop/mobile breakpoint.
 */
export function WorkspaceShell({
  topBar,
  inspector = false,
  defaultInspectorOpen = false,
  autoSaveId,
  children,
}: WorkspaceShellProps) {
  const { activeTabId, openSeq } = useInspector();
  const isDesktop = useMediaQuery(DESKTOP_QUERY);
  const [inspectorOpen, setInspectorOpen] = useState(defaultInspectorOpen);
  const lastOpenSeq = useRef(openSeq);
  const userCollapsedInspector = useRef(false);
  const inspectorPanelRef = useRef<PanelImperativeHandle | null>(null);

  // Re-open the inspector whenever openTab is called after mount (openSeq bumps
  // on every call, so even re-clicking the same citation re-opens a collapsed
  // pane). Ignore any active tab already present at mount so a cached provider
  // cannot override the caller's requested initial state.
  useEffect(() => {
    if (openSeq === lastOpenSeq.current) return;
    lastOpenSeq.current = openSeq;
    if (inspector && activeTabId && !userCollapsedInspector.current) {
      setInspectorOpen(true);
    }
  }, [activeTabId, inspector, openSeq]);

  const showInspector = inspector && inspectorOpen;
  const renderDesktopInspector = inspector && isDesktop;

  const groupRef = useRef<GroupImperativeHandle | null>(null);
  const didSeedInspector = useRef(false);

  // react-resizable-panels v4 removed the built-in `autoSaveId` persistence, so
  // we restore/save the panel split ourselves. `defaultLayout` is read once at
  // mount; `onLayoutChanged` fires after a drag completes (not mid-drag).
  const [defaultLayout] = useState<Record<string, number> | undefined>(() => {
    if (typeof window === "undefined") return undefined;
    try {
      const raw = window.localStorage.getItem(autoSaveId);
      return raw ? (JSON.parse(raw) as Record<string, number>) : undefined;
    } catch {
      return undefined;
    }
  });
  const initialInspectorLayout = useMemo(() => getInitialInspectorLayout(defaultLayout), [defaultLayout]);
  const panelGroupDefaultLayout = renderDesktopInspector
    ? defaultInspectorOpen
      ? initialInspectorLayout
      : { center: 100, inspector: 0 }
    : undefined;

  // Seed the desktop inspector split once the panel DOM is mounted. The panel
  // stays mounted and uses the library's collapsible API so opening it later
  // animates from collapsed size instead of remounting a new panel.
  useEffect(() => {
    if (!renderDesktopInspector || didSeedInspector.current) return;
    didSeedInspector.current = true;
    // Wait one frame so the panel DOM is mounted before calling setLayout.
    const rafId = requestAnimationFrame(() => {
      groupRef.current?.setLayout(initialInspectorLayout);
      if (defaultInspectorOpen) {
        inspectorPanelRef.current?.expand();
        return;
      }
      inspectorPanelRef.current?.collapse();
    });
    return () => cancelAnimationFrame(rafId);
  }, [renderDesktopInspector, defaultInspectorOpen, initialInspectorLayout]);

  useEffect(() => {
    if (!renderDesktopInspector) return;
    if (inspectorOpen) {
      inspectorPanelRef.current?.expand();
    } else {
      inspectorPanelRef.current?.collapse();
    }
  }, [inspectorOpen, renderDesktopInspector]);

  const persistLayout = useCallback(
    (layout: Record<string, number>) => {
      if (!("inspector" in layout) || layout.inspector <= 0) return;
      try {
        window.localStorage.setItem(autoSaveId, JSON.stringify(layout));
      } catch {
        // Ignore storage failures (private mode, quota exceeded).
      }
    },
    [autoSaveId],
  );

  const toggleInspector = useCallback(() => {
    setInspectorOpen((open) => {
      const nextOpen = !open;
      userCollapsedInspector.current = !nextOpen;
      return nextOpen;
    });
  }, []);

  const shellApi: WorkspaceShellContextValue = useMemo(
    () => ({
      inspectorEnabled: inspector,
      inspectorOpen,
      toggleInspector,
    }),
    [inspector, inspectorOpen, toggleInspector],
  );

  return (
    <WorkspaceShellContext.Provider value={shellApi}>
      <div className="flex h-screen min-h-0 flex-col">
        {topBar}
        <div className="min-h-0 flex-1">
          <ResizablePanelGroup
            orientation="horizontal"
            defaultLayout={panelGroupDefaultLayout}
            onLayoutChanged={persistLayout}
            groupRef={groupRef}
            className="h-full"
          >
            <ResizablePanel id="center" minSize={30} className="min-w-0 overflow-hidden">
              {children}
            </ResizablePanel>
            {renderDesktopInspector ? (
              <>
                {showInspector ? <ResizableHandle withHandle /> : null}
                <ResizablePanel
                  id="inspector"
                  minSize={20}
                  defaultSize={defaultInspectorOpen ? initialInspectorLayout.inspector : 0}
                  collapsible
                  collapsedSize={0}
                  panelRef={inspectorPanelRef}
                  className="min-w-0 overflow-hidden"
                >
                  <InspectorPane />
                </ResizablePanel>
              </>
            ) : null}
          </ResizablePanelGroup>
        </div>
      </div>
      {!isDesktop && showInspector ? (
        <div className="fixed inset-0 z-40 bg-background md:hidden">
          <InspectorPane />
        </div>
      ) : null}
    </WorkspaceShellContext.Provider>
  );
}

function getInitialInspectorLayout(defaultLayout: Record<string, number> | undefined): Record<string, number> {
  if (!defaultLayout || !("inspector" in defaultLayout)) {
    return { center: 50, inspector: 50 };
  }
  const inspectorSize = defaultLayout.inspector;
  if (!Number.isFinite(inspectorSize) || inspectorSize < 20 || inspectorSize >= 100) {
    return { center: 50, inspector: 50 };
  }
  return defaultLayout;
}
